// src/pages/CompanyManagement/tabs/OverviewTab.jsx
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon
} from '@mui/icons-material';

const OverviewTab = ({ company, t }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const stats = [
    {
      label: t('CompanyManagement.total_users'),
      value: company.userCount || 0,
      icon: <PeopleIcon color="primary" />,
      color: 'primary.main'
    },
    {
      label: t('CompanyManagement.total_locations'),
      value: company.locationCount || 0,
      icon: <LocationIcon color="secondary" />,
      color: 'secondary.main'
    },
    {
      label: t('CompanyManagement.status'),
      value: company.active ? t('CompanyManagement.active') : t('CompanyManagement.inactive'),
      icon: company.active ? <ActiveIcon color="success" /> : <InactiveIcon color="error" />,
      color: company.active ? 'success.main' : 'error.main'
    }
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('CompanyManagement.overview_title')}
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: `${stat.color.replace('.main', '.light')}15`,
                      display: 'flex'
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight="600">
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Company Information */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('CompanyManagement.company_information')}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('CompanyManagement.company_name_label')}
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {company.name}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('CompanyManagement.tenant_id')}
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {company.tenantId}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('CompanyManagement.created_date')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body1">
                    {formatDate(company.createdAt)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('CompanyManagement.last_updated')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body1">
                    {formatDate(company.updatedAt)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  {t('CompanyManagement.status')}
                </Typography>
                <Chip
                  icon={company.active ? <ActiveIcon /> : <InactiveIcon />}
                  label={company.active ? t('CompanyManagement.active') : t('CompanyManagement.inactive')}
                  color={company.active ? 'success' : 'error'}
                  size="small"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Retention Settings Summary */}
      {company.settings?.cameraFootageRetention && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('CompanyManagement.quick_settings_summary')}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('CompanyManagement.default_retention_period')}
                </Typography>
                <Typography variant="h6">
                  {company.settings.cameraFootageRetention.defaultRetentionDays || 30} {t('CompanyManagement.days')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('CompanyManagement.auto_delete_after_retention')}
                </Typography>
                <Chip
                  label={company.settings.cameraFootageRetention.autoDeleteAfterRetention ? t('CompanyManagement.enabled') : t('CompanyManagement.disabled')}
                  color={company.settings.cameraFootageRetention.autoDeleteAfterRetention ? 'success' : 'default'}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('CompanyManagement.enabled_support_types')}
                </Typography>
                <Typography variant="h6">
                  {company.settings.ticketSupportTypes?.filter(t => t.enabled).length || 0}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default OverviewTab;
